import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import {
  House, CheckCircle, Phone, EnvelopeSimple, Star, Clock, CurrencyEur, ArrowLeft,
  Sparkle, MapPin, User,
} from "@phosphor-icons/react";
import ProntoLogo from "@/components/ProntoLogo";

/**
 * MieProposte — pagina privata del proprietario per rivedere le proposte
 * ricevute dagli agenti. URL: /vendi-casa/proposte/:token
 *
 * Niente login: il token è il segreto. Conoscere il token = essere il
 * proprietario. Mostra le proposte degli agenti in card pulite, ordinate per
 * commissione/timing/rating, e permette di scegliere 1-3 agenti da sbloccare.
 */
export default function MieProposte() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [unlocked, setUnlocked] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await axios.get(`${BACKEND_URL}/api/marketplace/listings/by-token/${token}`);
        if (alive) setData(d);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Link non valido o annuncio scaduto.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const alreadySelected = (data?.pitches || []).some((p) => p.selected);

  const toggleSelect = (id) => {
    if (alreadySelected) return;
    setSelectedIds((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 3) {
        toast.error("Puoi scegliere al massimo 3 agenti.");
        return s;
      }
      return [...s, id];
    });
  };

  const confirmSelection = async () => {
    if (!selectedIds.length) return;
    setSubmitting(true);
    try {
      const { data: r } = await axios.post(
        `${BACKEND_URL}/api/marketplace/listings/by-token/${token}/select`,
        { pitch_ids: selectedIds },
      );
      setUnlocked(r);
      toast.success("Agenti selezionati. Ora possono contattarti.");
      // refresh
      const { data: d2 } = await axios.get(`${BACKEND_URL}/api/marketplace/listings/by-token/${token}`);
      setData(d2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella selezione.");
    } finally {
      setSubmitting(false);
    }
  };

  const renewListing = async () => {
    setRenewing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/marketplace/listings/by-token/${token}/renew`);
      toast.success("Annuncio rinnovato per altri 14 giorni!");
      const { data: d2 } = await axios.get(`${BACKEND_URL}/api/marketplace/listings/by-token/${token}`);
      setData(d2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel rinnovo.");
    } finally {
      setRenewing(false);
    }
  };

  if (loading) return <CenterScreen text="Carico le tue proposte…" />;
  if (!data) return <CenterScreen text="Link non valido o annuncio scaduto." />;

  const { listing, pitches, owner_name_first, days_left, status } = data;
  const isExpired = status === "expired" || days_left === 0;
  const isExpiringSoon = !isExpired && days_left <= 3;

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#1C1C1A]" data-testid="mie-proposte-page">
      <header className="sticky top-0 z-30 bg-[#FAF9F5]/90 backdrop-blur border-b border-[#EAE7DF]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/vendi-casa")} className="flex items-center gap-2 text-sm text-[#3F3D38] hover:text-[#1C1C1A]">
            <ProntoLogo size={28} />
            <span className="font-medium">Pronto</span>
          </button>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">Le tue proposte</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 lg:px-8 pt-8 pb-20">
        {/* Listing summary */}
        <div className="bg-white border border-[#EAE7DF] rounded-2xl p-5 sm:p-7 mb-5">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#7C8B6E] font-bold mb-1">
                <MapPin size={12} weight="fill" /> {listing.city}{listing.area ? ` · ${listing.area}` : ""}
              </div>
              <h1 className="font-editorial text-3xl sm:text-4xl leading-tight">
                Ciao {owner_name_first}, ecco le proposte.
              </h1>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-[#9A968B] font-semibold">Proposte arrivate</div>
              <div className="font-editorial text-3xl text-[#7C8B6E]">{pitches.length}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[13px] text-[#5A584F]">
            <span><strong className="text-[#1C1C1A]">{listing.surface_sqm}</strong> mq</span>
            <Dot/>
            <span><strong className="text-[#1C1C1A]">{listing.rooms}</strong> locali</span>
            {listing.bathrooms ? <><Dot/><span><strong className="text-[#1C1C1A]">{listing.bathrooms}</strong> bagni</span></> : null}
            {listing.state ? <><Dot/><span className="capitalize">{listing.state.replace("_", " ")}</span></> : null}
            {listing.target_price ? <><Dot/><span>obiettivo <strong className="text-[#1C1C1A]">€ {listing.target_price.toLocaleString("it-IT")}</strong></span></> : null}
          </div>
        </div>

        {/* TTL countdown / renew banner */}
        {(isExpired || isExpiringSoon) && (
          <div className={`rounded-2xl p-4 sm:p-5 mb-5 flex items-center gap-4 flex-wrap ${isExpired ? "bg-[#FFF6E5] border border-[#F5C97A]" : "bg-[#FFFBF0] border border-[#EAE0BC]"}`} data-testid="proposte-countdown">
            <Clock size={22} weight="duotone" className={isExpired ? "text-[#B97A1A]" : "text-[#9C7A1A]"}/>
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-[14px] text-[#1C1C1A]">
                {isExpired ? "Il tuo annuncio è scaduto." : `Scade tra ${days_left} ${days_left === 1 ? "giorno" : "giorni"}.`}
              </div>
              <div className="text-[12px] text-[#5A584F] mt-0.5">
                {isExpired
                  ? "Gli agenti non lo vedono più nel feed. Rinnova in 1 click per altri 14 giorni."
                  : "Dopo la scadenza l'annuncio sparisce dal feed degli agenti."}
              </div>
            </div>
            <button
              onClick={renewListing}
              disabled={renewing}
              className="px-4 py-2 rounded-md bg-[#1C1C1A] text-white text-[13px] font-semibold hover:bg-[#000] disabled:opacity-60"
              data-testid="proposte-renew"
            >
              {renewing ? "Rinnovo…" : "Rinnova 14 giorni"}
            </button>
          </div>
        )}

        {/* Unlocked contacts (post-selection) */}
        {unlocked && (
          <div className="bg-[#1C1C1A] text-white rounded-2xl p-5 sm:p-7 mb-8" data-testid="proposte-unlocked-banner">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A8B89A] font-bold mb-1">
              <CheckCircle size={14} weight="fill" /> Hai scelto {unlocked.selected_count} {unlocked.selected_count === 1 ? "agente" : "agenti"}
            </div>
            <h3 className="font-editorial text-xl mb-1">Adesso possono contattarti.</h3>
            <p className="text-[13px] text-white/65 leading-relaxed">
              Abbiamo sbloccato i tuoi contatti solo per loro. Gli altri agenti non sanno chi sei.
            </p>
          </div>
        )}

        {/* Empty state */}
        {pitches.length === 0 && (
          <div className="text-center py-16 bg-white border border-[#EAE7DF] rounded-2xl">
            <Clock size={32} className="mx-auto text-[#9A968B] mb-3" />
            <h3 className="font-editorial text-2xl mb-2">Ancora nessuna proposta.</h3>
            <p className="text-[14px] text-[#5A584F] max-w-md mx-auto">Le proposte degli agenti arrivano di solito entro 24-72 ore. Ti avviseremo via email appena ne ricevi una.</p>
          </div>
        )}

        {/* Pitches */}
        {pitches.length > 0 && (
          <>
            {!alreadySelected && (
              <p className="text-[13px] text-[#5A584F] mb-4">
                Confronta le proposte e seleziona <strong className="text-[#1C1C1A]">fino a 3 agenti</strong> da contattare. Gli altri non sapranno mai chi sei.
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {pitches.map((p) => (
                <PitchCard
                  key={p.id}
                  pitch={p}
                  token={token}
                  isSelected={selectedIds.includes(p.id)}
                  alreadySelected={alreadySelected || p.selected}
                  onToggle={() => toggleSelect(p.id)}
                />
              ))}
            </div>

            {!alreadySelected && selectedIds.length > 0 && (
              <div className="sticky bottom-4 mt-6 z-10">
                <div className="bg-[#1C1C1A] text-white rounded-xl p-4 shadow-xl flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A8B89A] font-bold">{selectedIds.length} {selectedIds.length === 1 ? "agente selezionato" : "agenti selezionati"}</div>
                    <div className="text-[13px] text-white/85 mt-0.5">Confermi? Solo loro vedranno i tuoi contatti.</div>
                  </div>
                  <button
                    onClick={confirmSelection}
                    disabled={submitting}
                    className="px-5 py-2.5 rounded-md bg-[#A8B89A] text-[#1C1C1A] font-semibold text-[14px] hover:bg-white transition-colors disabled:opacity-60"
                    data-testid="proposte-confirm"
                  >
                    {submitting ? "Sblocco in corso…" : "Conferma e sblocca contatti"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function PitchCard({ pitch, token, isSelected, alreadySelected, onToggle }) {
  return (
    <div
      className={`bg-white border rounded-2xl p-4 sm:p-5 transition-all ${
        isSelected
          ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/30"
          : pitch.selected
          ? "border-[#A8B89A] bg-[#F1F4ED]"
          : "border-[#EAE7DF]"
      }`}
      data-testid={`pitch-card-${pitch.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#F1F4ED] flex items-center justify-center shrink-0 overflow-hidden">
          {pitch.agent_picture ? (
            <img src={pitch.agent_picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <User size={18} className="text-[#7C8B6E]" weight="duotone" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[15px] truncate">{pitch.agent_name}</div>
          {pitch.agency_name && <div className="text-[11.5px] text-[#7C8B6E] truncate">{pitch.agency_name}</div>}
        </div>
        {pitch.selected && (
          <div className="text-[10px] px-1.5 py-0.5 rounded bg-[#7C8B6E] text-white font-bold uppercase tracking-wider">Scelto</div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <KPI icon={CurrencyEur} label="Commissione" value={`${pitch.proposed_commission_pct}%`} />
        <KPI icon={Star} label="Prezzo stim." value={`€${(pitch.listing_price_estimate/1000).toFixed(0)}k`} />
        <KPI icon={Clock} label="Tempo vendita" value={`${pitch.time_to_sale_weeks}sett`} />
      </div>

      <p className="text-[12.5px] text-[#3F3D38] leading-snug mb-3 line-clamp-4">{pitch.pitch_text}</p>

      {pitch.marketing_plan?.length > 0 && (
        <ul className="space-y-1 mb-4">
          {pitch.marketing_plan.slice(0, 3).map((b, i) => (
            <li key={i} className="text-[11.5px] text-[#5A584F] flex gap-1.5">
              <Sparkle size={10} weight="fill" className="text-[#7C8B6E] mt-1 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {!alreadySelected ? (
        <button
          onClick={onToggle}
          className={`w-full px-3 py-2 rounded-md text-[13px] font-semibold transition-colors ${
            isSelected
              ? "bg-[#7C8B6E] text-white"
              : "bg-[#F1F4ED] text-[#1C1C1A] hover:bg-[#E5EAD8]"
          }`}
          data-testid={`pitch-toggle-${pitch.id}`}
        >
          {isSelected ? "Selezionato · clicca per rimuovere" : "Voglio sentire questo agente"}
        </button>
      ) : pitch.selected ? (
        <div className="space-y-2">
          <div className="text-[12px] text-[#5A6A4D] flex items-center gap-2">
            <Phone size={14} weight="fill" /> Adesso vede i tuoi contatti
          </div>
          <ReviewBlock pitch={pitch} token={token} />
        </div>
      ) : (
        <div className="text-[11.5px] text-[#9A968B] italic">Hai scelto altri agenti per questa proposta.</div>
      )}
    </div>
  );
}

function ReviewBlock({ pitch, token }) {
  const [stars, setStars] = useState(pitch.my_review?.rating || 0);
  const [text, setText] = useState(pitch.my_review?.text || "");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(!!pitch.my_review);

  const send = async () => {
    if (!stars) {
      toast.error("Scegli da 1 a 5 stelle.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/marketplace/listings/by-token/${token}/review`, {
        pitch_id: pitch.id,
        rating: stars,
        text: text.trim() || null,
      });
      setDone(true);
      toast.success("Recensione inviata. Grazie!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-md p-2.5" data-testid={`review-done-${pitch.id}`}>
        <div className="text-[10.5px] uppercase tracking-widest text-[#5A6A4D] font-bold mb-1">La tua recensione</div>
        <div className="flex items-center gap-1 mb-0.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} size={13} weight={n <= stars ? "fill" : "regular"} className={n <= stars ? "text-[#D4A93B]" : "text-[#D4CFC4]"} />
          ))}
        </div>
        {text && <div className="text-[12px] text-[#3F3D38] italic">"{text}"</div>}
      </div>
    );
  }

  return (
    <div className="bg-[#FAF9F5] border border-[#EAE7DF] rounded-md p-2.5" data-testid={`review-form-${pitch.id}`}>
      <div className="text-[10.5px] uppercase tracking-widest text-[#7C8B6E] font-bold mb-1.5">
        Hai parlato con {pitch.agent_name.split(" ")[0]}? Aiuta gli altri
      </div>
      <div className="flex items-center gap-1 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setStars(n)}
            className="hover:scale-110 transition-transform"
            data-testid={`review-star-${pitch.id}-${n}`}
            aria-label={`${n} stelle`}
          >
            <Star size={18} weight={n <= stars ? "fill" : "regular"} className={n <= stars ? "text-[#D4A93B]" : "text-[#9A968B]"} />
          </button>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Optional: come ti sei trovato? (max 600 caratteri)"
        maxLength={600}
        className="w-full text-[12.5px] px-2 py-1.5 rounded-md border border-[#EAE7DF] bg-white text-[#1C1C1A] placeholder:text-[#9A968B] focus:outline-none focus:ring-1 focus:ring-[#7C8B6E]/40 focus:border-[#7C8B6E] min-h-[50px] resize-y mb-2"
        data-testid={`review-text-${pitch.id}`}
      />
      <button
        type="button"
        onClick={send}
        disabled={sending || !stars}
        className="w-full px-3 py-2 rounded-md bg-[#1C1C1A] text-white text-[12.5px] font-semibold hover:bg-[#000] disabled:opacity-60"
        data-testid={`review-submit-${pitch.id}`}
      >
        {sending ? "Invio…" : "Invia recensione"}
      </button>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#FAF9F5] rounded-md px-2 py-1.5 border border-[#EAE7DF]">
      <div className="text-[9px] uppercase tracking-widest text-[#9A968B] font-bold flex items-center gap-1">
        <Icon size={10} weight="bold" className="text-[#7C8B6E]" /> {label}
      </div>
      <div className="text-[13px] font-semibold text-[#1C1C1A] tabular-nums">{value}</div>
    </div>
  );
}

function Dot() {
  return <span className="text-[#9A968B]">·</span>;
}

function CenterScreen({ text }) {
  return (
    <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center text-[#5A584F]">{text}</div>
  );
}
